import { useCallback, useEffect, useState } from "react";

const FALLBACK_GRADIENT = "linear-gradient(135deg, #1E63C6 0%, #4A90E2 50%, #0a1e50 100%)";
const INTERVAL_MS = 5000;

export default function HeroCarousel({ slides = [] }) {
  const activeSlides = slides.filter((slide) => slide?.image_url);
  const hasImages = activeSlides.length > 0;
  const [current, setCurrent] = useState(0);
  const [fading, setFading] = useState(false);

  const goTo = useCallback(
    (index) => {
      if (!hasImages || index === current) return;
      setFading(true);
      setTimeout(() => {
        setCurrent(index);
        setFading(false);
      }, 400);
    },
    [current, hasImages]
  );

  const next = useCallback(() => {
    if (!hasImages) return;
    goTo((current + 1) % activeSlides.length);
  }, [activeSlides.length, current, goTo, hasImages]);

  useEffect(() => {
    if (!hasImages || activeSlides.length < 2) return undefined;
    const timer = setInterval(next, INTERVAL_MS);
    return () => clearInterval(timer);
  }, [activeSlides.length, hasImages, next]);

  useEffect(() => {
    if (current > activeSlides.length - 1) {
      setCurrent(0);
    }
  }, [activeSlides.length, current]);

  if (!hasImages) {
    return <div className="carousel__bg" style={{ background: FALLBACK_GRADIENT }} aria-hidden="true" />;
  }

  return (
    <div className="carousel" aria-label="Hero image carousel">
      {activeSlides.map((slide, i) => (
        <div
          key={slide.id || slide.image_url}
          className={`carousel__slide ${i === current ? (fading ? "carousel__slide--fading" : "carousel__slide--active") : ""}`}
          aria-hidden={i !== current}
        >
          <img
            src={slide.image_url}
            alt={slide.title || `Slide ${i + 1}`}
            className="carousel__img"
            loading={i === 0 ? "eager" : "lazy"}
          />
        </div>
      ))}

      <div className="carousel__overlay" aria-hidden="true" />

      {activeSlides.length > 1 && (
        <div className="carousel__dots" role="tablist" aria-label="Carousel navigation">
          {activeSlides.map((_, i) => (
            <button
              key={i}
              role="tab"
              aria-selected={i === current}
              aria-label={`Go to slide ${i + 1}`}
              className={`carousel__dot ${i === current ? "carousel__dot--active" : ""}`}
              onClick={() => goTo(i)}
              type="button"
            />
          ))}
        </div>
      )}

      {activeSlides.length > 1 && (
        <>
          <button
            className="carousel__arrow carousel__arrow--prev"
            onClick={() => goTo((current - 1 + activeSlides.length) % activeSlides.length)}
            aria-label="Previous slide"
            type="button"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>
          <button
            className="carousel__arrow carousel__arrow--next"
            onClick={next}
            aria-label="Next slide"
            type="button"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M9 18l6-6-6-6" />
            </svg>
          </button>
        </>
      )}
    </div>
  );
}
